/* eslint-disable @typescript-eslint/no-explicit-any */
import {Component, DestroyRef, OnInit, ViewChild} from '@angular/core';
import {PlanDetailsComponent} from './plan-details/plan-details.component';
import {TenureComponent} from './tenure/tenure.component';
import {LocalityAndLgaComponent} from './locality-and-lga/locality-and-lga.component';
import {CompiledFromComponent} from './compiled-from/compiled-from.component';
import {FieldRecordComponent} from './field-record/field-record.component';
import {SurveyDetailsComponent} from './survey-details/survey-details.component';
import {ProjectedCoordSystemComponent} from './projected-coord-system/projected-coord-system.component';
import {PlanningAuthorityComponent} from './planning-authority/planning-authority.component';
import {RoadComponent} from './road/road.component';
import {StrataSchemaDetailsComponent} from './strata-schema-details/strata-schema-details.component';
import {CrownStateLandComponent} from './crown-state-land/crown-state-land.component';
import {PlanModelService} from '../common/service/plan-model.service';

import {FormsModule, NgForm} from '@angular/forms';
import {UiConfigurationService} from '../common/service/ui-configuration.service';
import {AlertModalService, ConfirmationModalService} from '../common/component/modal';
import {Router} from '@angular/router';
import {PlanLodgementService} from '../common/service/plan-lodgement.service';
import {MetaData, NgEventBus} from 'ng-event-bus';
import {LookupService} from '../common/service/lookup.service';
import {FormerTenureCardinality} from '../common/models/former-tenure-cardinality.model';
import {PDF_CREATION_METHOD, PLAN_VALIDATION_STAGE} from '../common/constants';
import {UiConfiguration} from '../common/models/ui-configuration.model';
import {SurveyorDetailsComponent} from './surveyor-details/surveyor-details.component';
import {isDefined} from '../common/service/data-util';
import {NlrHasPermissionsPipe} from '../common/pipe/nlr-has-permissions.pipe';
import {PermissionsService} from '../common/service/permissions.service';
import {NlrPermissionsDisabledPipe} from '../common/pipe/nlr-permissions-disabled-pipe';
import {NlrPlanDetailService} from '../plan-detail/plan-detail.service';
import {concatMap, Observable, of, Subscription} from 'rxjs';
import {NotificationAlertsService} from '../common/notification-alerts/notification-alerts.service';
import {PlanPurposeLookup} from '../common/models/plan-purpose.model';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';

@Component({
    selector: 'nlr-plan-information',
    imports: [
        PlanDetailsComponent,
        SurveyorDetailsComponent,
        TenureComponent,
        LocalityAndLgaComponent,
        CompiledFromComponent,
        FieldRecordComponent,
        SurveyDetailsComponent,
        ProjectedCoordSystemComponent,
        PlanningAuthorityComponent,
        RoadComponent,
        StrataSchemaDetailsComponent,
        CrownStateLandComponent,
        FormsModule,
        NlrHasPermissionsPipe,
        NlrPermissionsDisabledPipe
    ],
    templateUrl: './plan-information.component.html',
    styleUrl: './plan-information.component.scss'
})
export class PlanInformationComponent implements OnInit {
    title = 'Information';
    savePermissions = ['EDIT_INFORMATION', 'VIEW_INFORMATION'];
    validatePermissions = ['EDIT_PLAN', 'VIEW_PLAN'];
    private formerTenureCardinalityLookups: FormerTenureCardinality[];
    private formerTenureCardinalityLookup?: FormerTenureCardinality;
    private groups: any = {};
    private modelUpdatesSubs: Subscription;
    @ViewChild('planInformationForm')
    planInformationForm: NgForm;
    informationUiConfig: UiConfiguration = {
        active: false,
        addFormerTenureModalLabel: '',
        crownStateLandDisplay: false,
        fieldRecordsDisplay: false,
        formerTenureGroupColumnLabels: '',
        formerTenureGroupLabel: '',
        id: '',
        localityLgaDisplay: false,
        parentTenureAssociationType: '',
        planCancellationDisplay: false,
        planDetailsDisplay: true,
        planPurposeCode: '',
        roadsDisplay: false,
        schemeParcelAddress: false,
        strataSchemeDetailsDisplay: false,
        strataSchemeModificationDisplay: false,
        surveyDetailsDisplay: false,
        surveyorDetailsDisplay: true,
        unapprovedPlansDisplay: false
    };
    planPurposeLookup: PlanPurposeLookup = {
        active: false,
        id: ''
    };

    get readonly() {
        return this.planModelService.readonly;
    }

    constructor(
        private destroyRef: DestroyRef,
        private planModelService: PlanModelService,
        private uiConfigurationService: UiConfigurationService,
        private confirmationService: ConfirmationModalService,
        private permissionsService: PermissionsService,
        private planLodgementService: PlanLodgementService,
        private alertModalService: AlertModalService,
        private lookupService: LookupService,
        private eventBus: NgEventBus,
        private router: Router,
        private planDetailsService: NlrPlanDetailService,
        private notificationAlertsService: NotificationAlertsService
    ) {}

    ngOnInit(): void {
        this.initialiseLookups();
        this.uiConfigurationService.populateInfoUiGroups();
        const uiGroup = this.uiConfigurationService.uiGroupsSetup();
        if (uiGroup.infoUiGroups) {
            this.informationUiConfig = uiGroup.infoUiGroups;
        }
        if (uiGroup.planPurposeLookup) {
            this.planPurposeLookup = uiGroup.planPurposeLookup;
        }
        this.modelUpdatesSubs = this.eventBus
            .on('nlr:plan-model-updated:*')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((meta: MetaData) => {
                switch (meta.data) {
                    case 'tenureType':
                    case 'planPurpose':
                    case 'planType': {
                        this.onChangePlanTypePlanPurpose();
                        this.updateUIGroupInfo();
                        break;
                    }
                    case 'surveyMethod': {
                        this.surveyMethodChanged();
                        break;
                    }
                    case 'model': {
                        this.updateUIGroupInfo();
                        break;
                    }
                }
            });

        this.eventBus
            .on('SAVE_INFORMATION')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this.save();
            });

        this.eventBus
            .on<boolean>('VALIDATE_INFORMATION')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(meta => this.validate(meta.data));

        this.eventBus
            .on('nlr:plan-model-updated:formerTenures')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.isFormerTenureCardinalityValid());
    }

    private updateUIGroupInfo() {
        const uiGroup = this.uiConfigurationService.uiGroupsSetup();
        this.planPurposeLookup = uiGroup.planPurposeLookup;
        if (uiGroup.infoUiGroups) {
            this.informationUiConfig = uiGroup.infoUiGroups;
        }
    }

    private initialiseLookups() {
        this.formerTenureCardinalityLookups = this.lookupService.getLookups('formerTenureCardinality');
    }

    getPlanLodgementModel() {
        return this.planModelService.getModel();
    }

    //TODO
    isReadOnly() {
        return this.readonly;
    }

    save() {
        console.debug(this.title + ' : save');
        if (!this.permissionsService.hasEditPermission(this.savePermissions) || this.saveDisabled()) {
            return;
        }
        const subscription = this.planLodgementService.save().subscribe({complete: () => subscription.unsubscribe()});
        this.eventBus.cast('nlr:busy', subscription);
    }

    getModel() {
        return this.planModelService.getModel();
    }

    cancel() {
        console.debug(this.title + ' : cancel');

        if (!this.isReadOnly() && this.planModelService.modelIsDirty()) {
            this.confirmationService
                .showModal({
                    title: 'Warning',
                    confirmationText: 'You are about to lose all changes. Are you sure you wish to continue?',
                    action: 'plan-information:loose_all_changes'
                })
                .subscribe(data => {
                    if (data === 'plan-information:loose_all_changes') {
                        console.log('Loose all changes');
                        this.router.navigate(['/plan-dashboard']);
                    }
                });
        }
    }

    saveDisabled() {
        return !this.hasMinimumPlanDetails() || this.readonly;
    }

    validateDisabled() {
        return !this.hasMinimumPlanDetails() || this.readonly;
    }

    isStrataSchemaDetailsEnabled() {
        return (
            this.informationUiConfig !== undefined &&
            this.informationUiConfig.strataSchemeDetailsDisplay &&
            ((isDefined(this.getPlanLodgementModel().formerTenures) && this.getPlanLodgementModel().formerTenures.length > 0) ||
                (this.getPlanLodgementModel().formerTenureSubject != undefined && this.getPlanLodgementModel().formerTenureSubject.id === 'CP'))
        );
    }

    isCompiledFromEnabled() {
        return (
            isDefined(this.getModel().surveyMethod) &&
            this.getModel().surveyMethod.name === 'Compiled' &&
            isDefined(this.getModel().compiledFromReferences) &&
            this.getModel().compiledFromReferences.length > 0
        );
    }
    isFormerTenureEnabled() {
        return this.planPurposeLookup?.parentTenureAssociationType !== undefined;
    }

    isLocalityEnabled() {
        return (
            this.informationUiConfig !== undefined &&
            this.informationUiConfig.localityLgaDisplay &&
            ((this.getPlanLodgementModel().formerTenures !== undefined && this.getPlanLodgementModel().formerTenures.length) ||
                (isDefined(this.getPlanLodgementModel().formerTenureSubject) && this.getPlanLodgementModel().formerTenureSubject.id === 'CP'))
        );
    }

    isSurveyDetailsEnabled() {
        return this.informationUiConfig !== undefined && this.informationUiConfig.surveyDetailsDisplay;
    }

    isDigitalPlan() {
        return this.getModel().pdfCreationMethod === PDF_CREATION_METHOD.DIGITAL_PLAN;
    }

    isFieldRecordEnabled() {
        return ['MANDATORY', 'OPTIONAL'].includes(this.getModel().surveyMethod?.fieldRecordRequired) && this.informationUiConfig.fieldRecordsDisplay;
    }

    isRoadsEnabled() {
        return (
            this.planPurposeLookup?.privateRoadQuestion ||
            this.planPurposeLookup?.roadClosureQuestion ||
            this.planPurposeLookup?.roadWideningQuestion ||
            this.planPurposeLookup?.roadNameApprovalQuestion ||
            this.planPurposeLookup?.roadNameApprovalExemptQuestion ||
            this.planPurposeLookup?.lgaApprovedUnnamedRoadQuestion
        );
    }

    isCrownStateLandEnabled() {
        return this.informationUiConfig !== undefined && this.informationUiConfig.crownStateLandDisplay;
    }

    hasMinimumPlanDetails() {
        return this.planLodgementService.hasMinimumPlanDetails();
    }

    onChangePlanTypePlanPurpose() {
        this.getModel().ssa = undefined;
        this.getModel().surveyMethod = undefined;
        this.getModel().localities = [{localityName: '', id: undefined}];
        this.getModel().localGovernmentAuthorities = [{lgaName: '', id: undefined}];
        this.getModel().fieldRecords = [{fieldRecordNumber: ''}];
        this.getModel().roads = [];
        this.getModel().planningAuthorityReferences = [];
        this.getModel().formerTenures = [];
        if (this.getModel().planLodgementStage !== 'LODGED') {
            this.getModel().dependantPlans = [];
            this.getModel().strataPlanActions = [];
        }
        this.getModel().certification = {variationApprovals: []};
        this.getModel().exemptedRoads = [];
        this.getModel().laaAcquisition = null;
        this.getModel().roadClosure = null;
        this.getModel().roadWidening = null;
        this.getModel().newOrExtendedRoads = null;
        this.getModel().lgaApprovedUnnamedRoad = null;
        this.getModel().approvalExemption = null;
        this.getModel().insetLots = null;
        this.getModel().numberOfInsetLots = undefined;

        // $scope.planInformationForm.$setPristine();
    }

    validate(skipSave = false) {
        console.log(this.title + ' : validate');
        this.planModelService.validated = true;
        if (!this.permissionsService.hasEditPermission(this.validatePermissions) || this.validateDisabled()) {
            return;
        }
        this.markAllAsDirty();
        if (this.planInformationForm.invalid) {
            this.notificationAlertsService.addMessage({
                subType: 'InformationForm',
                text: 'The Plan Information page is incomplete or data entered is invalid!'
            });
        }
        this.groups.planInformation = {}; // Resets plan information group settings
        const stage = PLAN_VALIDATION_STAGE.LODGEMENT_INFORMATION;
        let result: Observable<any>;
        if (this.planLodgementService.hasMinimumPlanDetails()) {
            if (this.planModelService.readonly || skipSave) {
                if (this.isFormerTenureCardinalityValid()) {
                    result = this.planDetailsService.runValidationsForStage(stage);
                } else {
                    result = of(true);
                }
            } else if (this.isFormerTenureCardinalityValid()) {
                result = this.planModelService.save().pipe(
                    concatMap(() => {
                        this.planLodgementService.increaseValidationAttempt();
                        return this.planDetailsService.runValidationsForStage(stage);
                    })
                );
            } else {
                result = of(true);
            }
        }
        const subs = result.subscribe();
        this.eventBus.cast('nlr:busy', subs);
    }

    private markAllAsDirty() {
        Object.keys(this.planInformationForm.controls).forEach(controlName => {
            const control = this.planInformationForm.controls[controlName];
            control.markAsDirty();
        });
    }

    isFormerTenureCardinalityValid() {
        let addedFormerTenuresCount = 0;

        if (this.getModel().formerTenures !== undefined) {
            addedFormerTenuresCount = this.getModel().formerTenures.length;
            this.getModel().formerTenures.filter(function (row) {
                addedFormerTenuresCount += row.lots.length > 1 ? row.lots.length - 1 : 0;
            });
        }

        console.debug(this.title + ' : former tenures size ' + addedFormerTenuresCount);
        this.formerTenureCardinalityLookup = this.getFormerTenureCardinalityLookup();

        if (this.formerTenureCardinalityLookup) {
            let minFormerTenure = this.formerTenureCardinalityLookup.minFormerTenure;
            if (
                this.planModelService.getModel().formerTenureSubject != null &&
                isDefined(this.planModelService.getModel().formerTenureSubject) &&
                isDefined(this.planModelService.getModel().formerTenureSubject.minFormerTenureOverride) &&
                this.planModelService.getModel().formerTenureSubject.minFormerTenureOverride != null
            ) {
                minFormerTenure = this.planModelService.getModel().formerTenureSubject.minFormerTenureOverride;
            }
            if (minFormerTenure > addedFormerTenuresCount) {
                const errorMessage = this.formerTenureCardinalityLookup.errorMessage;
                this.groups.planInformation.invalidFormerTenureText = errorMessage; // Sets former tenure invalid text
                this.eventBus.cast('planInformation:invalidFormerTenureText', errorMessage);
                return false;
            } else {
                this.eventBus.cast('planInformation:invalidFormerTenureText', '');
            }
        }

        return true;
    }

    getFormerTenureCardinalityLookup() {
        return this.formerTenureCardinalityLookups.filter(
            l =>
                l.tenureType === this.getModel().tenureType.name &&
                l.planType === this.getModel().planType.name &&
                l.planPurpose === this.getModel().planPurpose.name
        )[0];
    }

    surveyMethodChanged() {
        console.debug(this.title + ' : checking surveyMethod by watch!');

        if (this.getModel().surveyMethod?.fieldRecordRequired === 'NOT_REQUIRED') {
            this.getModel().fieldRecords = [];
        } else if (this.getModel().surveyMethod !== undefined) {
            if (this.getModel().fieldRecords.length == 0) {
                this.getModel().fieldRecords = [{fieldRecordNumber: ''}];
            }
        }
        if (this.getModel().surveyMethod !== undefined && this.getModel().surveyMethod.name === 'Compiled') {
            if (
                this.getModel().compiledFromReferences === undefined ||
                this.getModel().compiledFromReferences == null ||
                this.getModel().compiledFromReferences.length === 0
            ) {
                this.getModel().compiledFromReferences = [
                    {source: 'PLAN', reference: ''},
                    {source: 'FIELD', reference: ''},
                    {source: 'TITLE', reference: ''}
                ];
            }
        } else {
            this.getModel().compiledFromReferences = [];
        }
    }
}
